import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { ActualSelected, Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { InvestmentService } from '../../../../../modules/investment/investment.service';
import { InvestmentDialogPayComponent } from '../../../../../modules/investment/intangible-material/investment-dialog-pay.component';
import { AmortisationTableDialogComponent } from '../../../../../modules/investment/amortisation/table-dialog.component';
import {
  amortisationLabel,
  amortisationVariant,
  hasApiDate,
  InvestmentAssetDetail,
  InvestmentAssetRow,
  InvestmentAssetType,
  paymentLabel,
  paymentVariant,
  roundMoney,
  isNonDepreciable,
} from '../../models/investment-asset.model';
import { InvestmentAssetReadService } from '../../services/investment-asset-read.service';
import { InvestmentAssetEntityService } from '../../services/investment-asset-entity.service';

type BlockKind = 'payments' | 'entries' | 'schedule';

interface DetailLine {
  key: string;
  uid: number | null;
  date: string | Date | null;
  label: string;
  concept: string;
  meta: string;
  amount: number;
}

interface DetailBlock {
  kind: BlockKind;
  title: string;
  icon: string;
  total: number;
  count: number;
  lines: DetailLine[];
  canAdd: boolean;
  addLabel: string;
  canEdit: boolean;
  canDelete: boolean;
  emptyText: string;
  moreLabel: string;
}

const MAX_LINES = 5;

@Component({
  selector: 'app-investment-asset-detail',
  templateUrl: './investment-asset-detail.component.html',
  styleUrls: ['./investment-asset-detail.component.scss'],
  standalone: false,
})
export class InvestmentAssetDetailComponent implements OnChanges {
  @Input() row!: InvestmentAssetRow;
  @Input() type: InvestmentAssetType = 1;
  @Output() changed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();

  readonly maxLines = MAX_LINES;
  readonly year = Number(ActualSelected.yearSelected) || new Date().getFullYear();

  loading = true;
  error = false;
  detail: InvestmentAssetDetail | null = null;
  blocks: DetailBlock[] = [];

  amortized = 0;
  amortizedYear = 0;

  private loadedSignature = '';

  constructor(
    private reads: InvestmentAssetReadService,
    private entity: InvestmentAssetEntityService,
    private investmentService: InvestmentService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get paymentLabel(): string {
    return paymentLabel(this.row);
  }

  get paymentVariant() {
    return paymentVariant(this.row);
  }

  get amortisationLabel(): string {
    return amortisationLabel(this.row);
  }

  get amortisationVariant() {
    return amortisationVariant(this.row);
  }

  get paidPercent(): number {
    const total = Number(this.row && this.row.total) || 0;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, (Number(this.row.paid) || 0) * 100 / total));
  }

  get pendingAmount(): number {
    return Math.max(Number(this.row && this.row.pending) || 0, 0);
  }

  get pendingAmortization(): number {
    return roundMoney(Math.max((Number(this.row && this.row.totalWithoutTax) || 0) - this.amortized, 0));
  }

  get hasDocument(): boolean {
    return !!this.row && (this.row.image === true || this.row.pdf === true);
  }

  date(value: any): string {
    return hasApiDate(value) ? formatDate(value, 'dd/MM/yyyy', 'es-ES') : '—';
  }

  ngOnChanges(): void {
    if (!this.row || !this.row.uid) return;
    const signature = [
      this.row.uid, this.row.total, this.row.paid, this.row.totalWithoutTax, this.row.amortisationYearExists,
      this.row.maximumLinealCoeficient, this.row.purchaseDate,
    ].join('|');
    if (signature === this.loadedSignature) return;
    this.loadedSignature = signature;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(block: DetailBlock): void {
    if (block.kind === 'payments') this.openPay();
    if (block.kind === 'entries') this.addAmortisationAccounting();
    if (block.kind === 'schedule') this.openSchedule();
  }

  manage(block: DetailBlock): void {
    if (block.kind === 'payments') this.openPay();
    else this.openSchedule();
  }

  editLine(block: DetailBlock, line: DetailLine): void {
    if (block.kind === 'payments' && line.uid !== null) this.openPay(line.uid);
  }

  deleteLine(block: DetailBlock, line: DetailLine): void {
    if (!this.tier.requireDocumentsTier()) return;
    if (block.kind === 'payments' && line.uid !== null) {
      this.confirm.open({
        title: 'Eliminar pago',
        subtitle: this.subtitle() || undefined,
        message: 'Se eliminará el pago y su asiento contable, y la inversión volverá a quedar pendiente de pago. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
        tone: 'danger',
        details: this.lineDetails(line, 'Importe'),
        confirmText: 'Eliminar',
        confirmIcon: 'delete',
        loadingText: 'Eliminando pago…',
        errorText: 'Ha ocurrido un error al eliminar el pago',
        action: () => this.investmentService.investmentDepositDelete(line.uid as number).then(ok => { if (ok !== true) throw new Error(); }),
      }).subscribe(deleted => {
        if (!deleted) return;
        this.notify.ok('Pago eliminado correctamente');
        this.changed.emit();
      });
      return;
    }
    if (block.kind === 'entries') {
      this.confirm.open({
        title: 'Eliminar contabilización de la amortización',
        subtitle: this.subtitle() || undefined,
        message: `Se eliminará el asiento de amortización del ejercicio ${this.year} de esta inversión.`,
        tone: 'danger',
        details: this.lineDetails(line, 'Importe'),
        confirmText: 'Eliminar',
        confirmIcon: 'delete',
        loadingText: 'Eliminando asiento de amortización…',
        errorText: 'No se ha eliminado la contabilidad de la amortización',
        action: () => this.entity.deleteAmortisationAccounting(this.row.uid),
      }).subscribe(deleted => {
        if (!deleted) return;
        this.notify.ok('Se ha eliminado correctamente la contabilidad de la amortización');
        this.changed.emit();
      });
    }
  }

  openSchedule(): void {
    if (!this.tier.requireDocumentsTier()) return;
    AmortisationTableDialogComponent.open(this.dialog, { uid: this.row.uid, name: this.row.description, year: this.year }).subscribe();
  }

  trackByKey(_: number, line: DetailLine): string {
    return line.key;
  }

  trackByKind(_: number, block: DetailBlock): string {
    return block.kind;
  }

  private openPay(editUid?: number): void {
    if (!this.tier.requireDocumentsTier()) return;
    InvestmentDialogPayComponent.open(this.dialog, {
      investment: { uid: this.row.uid, total: this.row.total, description: this.row.description, supplier: this.row.supplier },
      editUid,
    }).subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private addAmortisationAccounting(): void {
    if (!this.tier.requireDocumentsTier()) return;
    let message = '';
    this.confirm.open({
      title: 'Contabilizar amortización',
      subtitle: this.subtitle() || undefined,
      message: `Se generará el asiento de amortización del ejercicio ${this.year} de esta inversión.`,
      tone: 'info',
      icon: 'show_chart',
      details: [
        { label: 'Cuenta', value: this.row.account || '—' },
        { label: 'Según cuadro ' + this.year, value: Utils.FormatMoney(this.amortizedYear) + ' €' },
      ],
      confirmText: 'Contabilizar',
      confirmIcon: 'check_circle',
      loadingText: 'Contabilizando amortización…',
      errorText: 'No se pudo contabilizar la amortización',
      action: () => this.entity.addAmortisationAccounting(this.row.uid).then(result => { message = result; }),
    }).subscribe(done => {
      if (!done) return;
      this.notify.ok(message || 'Amortización contabilizada correctamente');
      this.changed.emit();
    });
  }

  private load(uid: string): void {
    this.loading = !this.detail;
    this.error = false;
    this.reads.detail(uid)
      .then(detail => {
        if (!this.row || uid !== this.row.uid) return;
        this.detail = detail;
        this.blocks = this.buildBlocks(detail);
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private buildBlocks(detail: InvestmentAssetDetail): DetailBlock[] {
    const deposits = Array.isArray(detail.deposits) ? detail.deposits : [];
    const amortization = Array.isArray(detail.amortization) ? detail.amortization : [];
    const entries = Array.isArray(detail.amortizationEntries) ? detail.amortizationEntries : [];

    this.amortized = roundMoney(amortization.reduce((acc, line) => acc + (Number(line.amount) || 0), 0));
    const yearLines = amortization.filter(line => Number(line.year) === this.year);
    this.amortizedYear = roundMoney(yearLines.reduce((acc, line) => acc + (Number(line.amount) || 0), 0));

    const paymentLines: DetailLine[] = deposits.map(item => ({
      key: 'p' + item.uid,
      uid: item.uid,
      date: item.depositDate,
      label: '',
      concept: item.typePay || 'Sin forma de pago',
      meta: [item.bankAccountInfo || '', item.commissions ? 'Comisión ' + Utils.FormatMoney(item.commissions) + ' €' : ''].filter(Boolean).join(' · '),
      amount: Number(item.amount) || 0,
    }));

    const entryLines: DetailLine[] = entries.map(item => ({
      key: 'e' + item.accountEntry,
      uid: item.accountEntry,
      date: item.date,
      label: '',
      concept: 'Asiento nº ' + item.accountEntry,
      meta: item.description || '',
      amount: Number(item.amount) || 0,
    }));

    const scheduleLines: DetailLine[] = yearLines.map((item, index) => ({
      key: 's' + index,
      uid: null,
      date: null,
      label: item.month,
      concept: item.month + ' ' + item.year,
      meta: '',
      amount: Number(item.amount) || 0,
    }));

    return [
      this.block('payments', 'Pagos', 'payment', paymentLines, {
        canAdd: this.pendingAmount > 0 || !paymentLines.length,
        addLabel: 'Añadir',
        canEdit: true,
        canDelete: true,
        emptyText: 'Sin pagos registrados',
        moreLabel: 'pagos',
      }),
      this.block('entries', 'Amortización contable ' + this.year, 'show_chart', entryLines, {
        canAdd: !entryLines.length && !this.row.amortisationYearExists && !isNonDepreciable(this.row),
        addLabel: 'Contabilizar',
        canEdit: false,
        canDelete: true,
        emptyText: isNonDepreciable(this.row) ? 'Los terrenos no se amortizan' : 'Sin asiento de amortización en el ejercicio',
        moreLabel: 'asientos',
      }),
      this.block('schedule', 'Cuadro de amortización ' + this.year, 'insert_chart', scheduleLines, {
        canAdd: true,
        addLabel: 'Ver cuadro',
        canEdit: false,
        canDelete: false,
        emptyText: 'Sin cuotas de amortización en el ejercicio',
        moreLabel: 'meses',
      }),
    ];
  }

  private block(kind: BlockKind, title: string, icon: string, lines: DetailLine[], options: Pick<DetailBlock, 'canAdd' | 'addLabel' | 'canEdit' | 'canDelete' | 'emptyText' | 'moreLabel'>): DetailBlock {
    return {
      kind,
      title,
      icon,
      total: roundMoney(lines.reduce((acc, line) => acc + line.amount, 0)),
      count: lines.length,
      lines: lines.slice(0, MAX_LINES),
      ...options,
    };
  }

  private subtitle(): string {
    return [this.row.description, this.row.supplier].filter(Boolean).join(' · ');
  }

  private lineDetails(line: DetailLine, amountLabel: string): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (hasApiDate(line.date)) details.push({ label: 'Fecha', value: formatDate(line.date as any, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Concepto', value: line.concept + (line.meta ? ' · ' + line.meta : '') });
    details.push({ label: amountLabel, value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }
}
